// 261005 E3-P3b — thẻ khối chặng ③ Bản địa hoá: danh sách lệnh + tóm tắt ảnh hưởng + mục gập "Ảnh hưởng chi tiết" (LUÔN có: tập bị đổi + số chỗ, chương giữ nguyên / cập nhật, cảnh báo, lỗi chặn)
// · "Lệnh không áp được" · Xem thử / Áp (Popconfirm nhắc lại số khi đụng kịch bản) / Khôi phục bản trước / Áp lại. Mọi quyết định ở `localizeBlockView.ts` (hàm thuần, có jest); file này chỉ vẽ.
import React from 'react';
import { Alert, Collapse, Popconfirm, Tooltip } from 'antd';
import Button from '../WbButton';
import type { AssistantBlock } from '../../../../types/adaptationAssistant';
import { localizeCardView } from './localizeBlockView';
import { disabledTip } from './assistantView';
import { BlockBody, BlockFoot, BlockShell } from './BlockShell';
import type { TimelineHandlers } from './AssistantTimeline'; // chỉ kiểu — không vòng phụ thuộc lúc chạy

const ALERT_TYPE: Record<string, 'error' | 'warning' | 'info' | 'success'> = { error: 'error', warning: 'warning', info: 'info', success: 'success' };

interface Props extends TimelineHandlers {
  block: AssistantBlock;
  headModifiedAt?: string | null;
  busy: boolean;
  acting: string | null;
  busyReason?: string | null;
}

const LocalizeBlockCard: React.FC<Props> = ({ block, headModifiedAt, busy, acting, busyReason, onPreview, onApply, onUndo, onReapply, onDismiss }) => {
  const v = localizeCardView(block, busy || !!acting, headModifiedAt, busyReason);
  const why = (busy && busyReason ? busyReason : undefined) ?? v.applyBlockedReason ?? undefined;
  const applyBtn = (
    <Tooltip title={disabledTip(v.canApply, why)}><Button size="small" type="primary" disabled={!v.canApply} loading={acting === `apply-${block.id}`} onClick={v.needsConfirm ? undefined : () => onApply(block)}>Áp</Button></Tooltip>
  );
  return (
    <BlockShell>
      <BlockBody title={block.title} tone={v.tone} stateLabel={v.headline} running={v.phase === 'running'}>
      {v.opLabels.length > 1 && <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 13 }}>{v.opLabels.map((l, i) => <li key={i} style={{ wordBreak: 'break-word' }}>{l}</li>)}</ul>}
      {v.detail && <div style={{ fontSize: 13, color: 'var(--wb-text-2)', margin: '4px 0', wordBreak: 'break-word' }}>{v.detail}</div>}
      {v.summary && <div style={{ fontSize: 13, margin: '4px 0', wordBreak: 'break-word' }}>{v.summary}</div>}
      {v.sections.length > 0 && (
        <Collapse
          size="small" ghost style={{ margin: '2px -8px' }}
          items={[{
            key: 'impact', label: 'Ảnh hưởng chi tiết',
            children: v.sections.map((s) => (
              <Alert
                key={s.key} style={{ margin: '4px 0' }} type={ALERT_TYPE[s.tone]} showIcon={false}
                message={<span style={{ fontSize: 13, fontWeight: 600 }}>{s.title}</span>}
                description={s.lines.map((l, i) => <div key={i} style={{ fontSize: 13, wordBreak: 'break-word' }}>{l}</div>)}
              />
            )),
          }]}
        />
      )}
      {v.keepLockNote && v.phase === 'ready' && <Alert style={{ margin: '6px 0' }} type="warning" showIcon message={v.keepLockNote} />}
      {v.applyBlockedReason && v.phase === 'ready' && <Alert style={{ margin: '6px 0' }} type="error" showIcon message="Không áp được" description={v.applyBlockedReason} />}
      {v.handoffWarning && <Alert style={{ margin: '6px 0' }} type="warning" showIcon message={v.handoffWarning} />}
      {v.rejected.length > 0 && (
        <Alert style={{ margin: '6px 0' }} type="info" showIcon message="Lệnh không áp được" description={v.rejected.map((r, i) => <div key={i} style={{ fontSize: 13 }}>{r.label}: {r.reason}</div>)} />
      )}
      </BlockBody>
      <BlockFoot>
        {v.canPreview && <Button size="small" type="text" loading={acting === `preview-${block.id}`} onClick={() => onPreview(block)}>{v.previewLabel}</Button>}
        {!v.canPreview && busy && busyReason && (v.phase === 'proposed' || v.phase === 'restored' || v.phase === 'failed' || v.phase === 'stale') && (
          <Tooltip title={busyReason}><Button size="small" type="text" disabled>{v.previewLabel}</Button></Tooltip>
        )}
        {v.phase === 'ready' && (v.needsConfirm
          ? (
            <Popconfirm title={v.confirmTitle} description={v.confirmDescription} okText="Áp" cancelText="Thôi" onConfirm={() => onApply(block)} disabled={!v.canApply}>
              {applyBtn}
            </Popconfirm>
          )
          : applyBtn)}
        {v.phase === 'applied' && (
          <Tooltip title={disabledTip(v.canUndo, v.undoReason ?? v.undoHint)}>
            <span>
              <Popconfirm title="Khôi phục bản trước?" description={v.undoHint} okText="Khôi phục" cancelText="Thôi" onConfirm={() => onUndo(block)} disabled={!v.canUndo}>
                <Button size="small" type="text" disabled={!v.canUndo} loading={acting === `undo-${block.id}`}>Khôi phục bản trước</Button>
              </Popconfirm>
            </span>
          </Tooltip>
        )}
        {v.phase === 'restored' && (
          <Popconfirm title="Áp lại đề xuất này?" description="Không tốn AI; chỉ áp được khi tên / từ vựng chưa khác lúc đề xuất." okText="Áp lại" cancelText="Thôi" onConfirm={() => onReapply(block)} disabled={!v.canReapply}>
            <Tooltip title={disabledTip(v.canReapply, busy && busyReason ? busyReason : undefined)}><Button size="small" type="primary" disabled={!v.canReapply} loading={acting === `reapply-${block.id}`}>Áp lại</Button></Tooltip>
          </Popconfirm>
        )}
        {v.canDismiss && <Button size="small" type="text" loading={acting === `dismiss-${block.id}`} onClick={() => onDismiss(block)}>Bỏ</Button>}
      </BlockFoot>
    </BlockShell>
  );
};

export default LocalizeBlockCard;
